import { Loader2, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useTeamChatSearch } from '../../hooks/useTeamChat';
import { Avatar } from '../tasks/linear/Avatar';

/**
 * Body of the in-conversation search panel: find a message in *this* thread and
 * scroll to it. Anchoring and dismissal belong to the shared `Popover`, exactly
 * like PinnedPanel.
 *
 * Jumping reuses `useJumpToMessage` from the view above, which already pages the
 * history backwards until the message is mounted — so a hit from months ago
 * works the same as one from this morning.
 */
export function ChatSearchPanel({
  conversationId,
  onJumpTo,
  onClose,
}: {
  conversationId: string;
  onJumpTo: (messageId: string) => void;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  // Search hits the database, so wait for a pause in typing rather than firing
  // a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(handle);
  }, [query]);

  const { data, isFetching } = useTeamChatSearch(debounced, conversationId);
  const results = data ?? [];
  const searched = debounced.trim().length >= 2;

  return (
    <div>
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-2">
        <Search size={14} className="shrink-0 text-muted-foreground" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('teamChat.searchInChat')}
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.cancel')}
          className="touch-target-auto shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      <div className="max-h-80 overflow-y-auto p-1">
        {results.map((result) => (
          <button
            key={result.messageId}
            type="button"
            onClick={() => {
              onJumpTo(result.messageId);
              onClose();
            }}
            className="touch-target-auto flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent"
          >
            <Avatar name={result.authorName || null} size={24} />
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-1.5">
                <span className="truncate text-xs font-semibold text-foreground">
                  {result.authorName}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {new Date(result.createdAt).toLocaleDateString(locale)}
                </span>
              </span>
              <span className="line-clamp-2 block text-xs text-muted-foreground">
                {result.snippet}
              </span>
            </span>
          </button>
        ))}
        {/* Only ever one of these three: a spinner while the first page of a new
            query is in flight, "nothing found" once it has answered, and the
            hint until the query is long enough to be worth sending. */}
        {isFetching && results.length === 0 ? (
          <p className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-muted-foreground">
            <Loader2 size={13} className="animate-spin" />
            {t('teamChat.searching')}
          </p>
        ) : (
          results.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              {searched ? t('teamChat.searchNoResults') : t('teamChat.searchInChat')}
            </p>
          )
        )}
      </div>
    </div>
  );
}
